import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Lightbulb,
  Search,
  Cog,
  Users,
  Calendar as CalendarIcon,
  Megaphone,
  Wallet,
  ShieldAlert,
  Grid3x3,
  LogOut,
  Download,
  FileDown,
  Loader2,
  LayoutDashboard,
  Presentation,
  Wand2,
  Share2,
  Copy,
  Check,
  UserPlus,
  MoreHorizontal,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { SecondaryButton, PrimaryButton, Overline } from "@/components/ui-kit";
import CollaboratorsModal from "@/components/CollaboratorsModal";
import SaveIndicator from "@/components/SaveIndicator";
import { useAutoSave } from "@/hooks/useAutoSave";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

import ExecutiveSummaryModule from "@/modules/ExecutiveSummary";
import CanvasModule from "@/modules/Canvas";
import ConceptModule from "@/modules/Concept";
import MarketModule from "@/modules/Market";
import OperationsModule from "@/modules/Operations";
import GYAModule from "@/modules/GYA";
import HRModule from "@/modules/HR";
import CalendarModule from "@/modules/CalendarModule";
import MarketingModule from "@/modules/Marketing";
import BudgetModule from "@/modules/Budget";
import RisksModule from "@/modules/Risks";
import FullPlanDocument from "@/components/FullPlanDocument";
import OneSheet from "@/components/OneSheet";

import { Globe2 } from "lucide-react";

// Full set of modules. `leanOnly` flag = visible in Lean mode.
const MODULES = [
  { key: "executive_summary", icon: FileText, component: ExecutiveSummaryModule, leanOnly: true },
  { key: "canvas", icon: Grid3x3, component: CanvasModule, leanOnly: true },
  { key: "concept", icon: Lightbulb, component: ConceptModule, leanOnly: true },
  { key: "market", icon: Search, component: MarketModule, leanOnly: true },
  { key: "operations", icon: Cog, component: OperationsModule, leanOnly: false },
  { key: "gya", icon: Globe2, component: GYAModule, leanOnly: true },
  { key: "hr", icon: Users, component: HRModule, leanOnly: false },
  { key: "calendar", icon: CalendarIcon, component: CalendarModule, leanOnly: false },
  { key: "marketing", icon: Megaphone, component: MarketingModule, leanOnly: false },
  { key: "budget", icon: Wallet, component: BudgetModule, leanOnly: true },
  { key: "risks", icon: ShieldAlert, component: RisksModule, leanOnly: true },
];

export default function ProjectView() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("executive_summary");
  const [exportingFull, setExportingFull] = useState(false);
  const [showOneSheet, setShowOneSheet] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showCollabs, setShowCollabs] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [planMode, setPlanMode] = useState(() => {
    try {
      return localStorage.getItem("plan_mode") || "full";
    } catch {
      return "full";
    }
  });
  const moduleRef = useRef(null);
  const fullPlanRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem("plan_mode", planMode);
    } catch (e) { void e; }
  }, [planMode]);

  const visibleModules = planMode === "lean" ? MODULES.filter((m) => m.leanOnly) : MODULES;
  // Keep active valid if user switches mode while on a hidden module
  useEffect(() => {
    if (!visibleModules.some((m) => m.key === active)) {
      setActive(visibleModules[0]?.key || "executive_summary");
    }
  }, [planMode, active, visibleModules]);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${id}`);
      setProject(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      nav("/");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function saveModule(moduleKey, data) {
    // Quiet save (used by auto-save). No toast — the SaveIndicator chip
    // already communicates state. Errors bubble up so useAutoSave can flip
    // its status to "error".
    const res = await api.put(`/projects/${id}/modules/${moduleKey}`, { data });
    setProject(res.data);
  }

  async function exportPdf() {
    if (!moduleRef.current) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const jsPDF = (await import("jspdf")).jsPDF;
      const node = moduleRef.current;
      const canvas = await html2canvas(node, { background: "#ffffff", scale: 2 });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW - 40;
      const imgH = (canvas.height * imgW) / canvas.width;
      let y = 20;
      if (imgH <= pageH - 40) {
        pdf.addImage(img, "PNG", 20, y, imgW, imgH);
      } else {
        // Single tall image — let it run multi-page by slicing
        const ratio = imgW / canvas.width;
        const sliceH = (pageH - 40) / ratio;
        let yOff = 0;
        while (yOff < canvas.height) {
          const c2 = document.createElement("canvas");
          c2.width = canvas.width;
          c2.height = Math.min(sliceH, canvas.height - yOff);
          const ctx = c2.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, c2.width, c2.height);
          ctx.drawImage(canvas, 0, -yOff);
          const sliceImg = c2.toDataURL("image/png");
          pdf.addImage(sliceImg, "PNG", 20, 20, imgW, (c2.height * imgW) / canvas.width);
          yOff += sliceH;
          if (yOff < canvas.height) pdf.addPage();
        }
      }
      pdf.save(`${project.name}-${active}.pdf`);
    } catch (e) {
      toast.error("PDF: " + e.message);
    }
  }

  async function exportFullPdf() {
    if (!fullPlanRef.current) return;
    setExportingFull(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const jsPDF = (await import("jspdf")).jsPDF;
      const node = fullPlanRef.current;
      // Make the node visible during capture
      node.style.position = "fixed";
      node.style.left = "0";
      node.style.top = "0";
      node.style.zIndex = "-1";
      node.style.opacity = "1";
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(node, { background: "#ffffff", scale: 2 });
      // Hide again
      node.style.opacity = "0";
      node.style.left = "-10000px";

      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW - 40;
      const ratio = imgW / canvas.width;
      const sliceH = (pageH - 40) / ratio;
      let yOff = 0;
      while (yOff < canvas.height) {
        const c2 = document.createElement("canvas");
        c2.width = canvas.width;
        c2.height = Math.min(sliceH, canvas.height - yOff);
        const ctx = c2.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, c2.width, c2.height);
        ctx.drawImage(canvas, 0, -yOff);
        const sliceImg = c2.toDataURL("image/png");
        pdf.addImage(sliceImg, "PNG", 20, 20, imgW, (c2.height * imgW) / canvas.width);
        yOff += sliceH;
        if (yOff < canvas.height) pdf.addPage();
      }
      pdf.save(`${project.name}-business-plan.pdf`);
      toast.success(lang === "es" ? "Plan exportado" : "Plan exported");
    } catch (e) {
      toast.error("PDF: " + e.message);
    } finally {
      setExportingFull(false);
    }
  }

  if (loading || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="text-[#5C5B5A] text-sm tracking-[0.2em] uppercase">{t("common.loading")}</div>
      </div>
    );
  }

  async function duplicateProject() {
    setDuplicating(true);
    try {
      const res = await api.post(`/projects/${id}/duplicate`);
      toast.success(lang === "es" ? `Versión creada: ${res.data.name}` : `Version created: ${res.data.name}`);
      nav(`/projects/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDuplicating(false);
    }
  }

  const ActiveComp = MODULES.find((m) => m.key === active)?.component;
  const moduleData = project.modules?.[active] || {};

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="border-b border-[#E8E6E1] bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to="/"
              className="text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors flex items-center gap-1 text-sm"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">{t("common.back")}</span>
            </Link>
            <div className="hidden sm:block h-6 w-px bg-[#E8E6E1]" />
            <div className="min-w-0">
              <Overline>
                {project.is_owner === false
                  ? (lang === "es" ? "Compartido contigo" : "Shared with you")
                  : (lang === "es" ? "Proyecto" : "Project")}
              </Overline>
              <h1
                className="text-xl sm:text-2xl tracking-tight text-[#1A1A1A] truncate"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                data-testid="project-title"
              >
                {project.name}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/projects/${id}/pitch-deck`}>
              <SecondaryButton testId="open-pitch-deck-btn" className="whitespace-nowrap">
                <Presentation className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span className="hidden xl:inline">{lang === "es" ? "Pitch deck" : "Pitch deck"}</span>
              </SecondaryButton>
            </Link>
            <SecondaryButton
              onClick={() => setShowCollabs(true)}
              testId="open-collaborators-btn"
              className="whitespace-nowrap"
            >
              <UserPlus className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              <span className="hidden xl:inline">{lang === "es" ? "Colaboradores" : "Collaborators"}</span>
              {(project.collaborators || []).length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#2563EB] text-[#F9F8F6] text-[10px] px-1 font-mono">
                  {(project.collaborators || []).length}
                </span>
              )}
            </SecondaryButton>
            {project.is_owner !== false && (
              <SecondaryButton
                onClick={() => setShowShare(true)}
                testId="open-share-btn"
                className="whitespace-nowrap"
              >
                <Share2 className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span className="hidden xl:inline">{lang === "es" ? "Compartir" : "Share"}</span>
              </SecondaryButton>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 border border-[#E8E6E1] bg-white text-[#1A1A1A] hover:border-[#2563EB] rounded-sm px-3 py-2 text-sm transition-all whitespace-nowrap"
                  data-testid="project-more-menu-btn"
                  aria-label={lang === "es" ? "Más acciones" : "More actions"}
                >
                  <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
                  <span className="hidden xl:inline">{lang === "es" ? "Más" : "More"}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-60 border-[#E8E6E1] bg-white rounded-sm shadow-lg"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#8A8886] font-medium">
                  {lang === "es" ? "Documentos" : "Documents"}
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={() => setShowOneSheet(true)}
                  data-testid="onesheet-open-btn"
                  className="cursor-pointer focus:bg-[#FBFAF7]"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2 text-[#5C5B5A]" strokeWidth={1.5} />
                  {t("onesheet.button")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={exportFullPdf}
                  data-testid="export-full-pdf-btn"
                  disabled={exportingFull}
                  className="cursor-pointer focus:bg-[#FBFAF7]"
                >
                  {exportingFull ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin text-[#5C5B5A]" strokeWidth={1.5} />
                  ) : (
                    <FileDown className="h-4 w-4 mr-2 text-[#5C5B5A]" strokeWidth={1.5} />
                  )}
                  {t("common.export_full")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={exportPdf}
                  data-testid="export-pdf-btn"
                  className="cursor-pointer focus:bg-[#FBFAF7]"
                >
                  <Download className="h-4 w-4 mr-2 text-[#5C5B5A]" strokeWidth={1.5} />
                  {t("common.export_pdf")}
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-[#E8E6E1]" />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#8A8886] font-medium">
                  {lang === "es" ? "Herramientas" : "Tools"}
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={() => nav(`/projects/${id}/branding`)}
                  data-testid="open-branding-btn"
                  className="cursor-pointer focus:bg-[#FBFAF7]"
                >
                  <Wand2 className="h-4 w-4 mr-2 text-[#5C5B5A]" strokeWidth={1.5} />
                  {lang === "es" ? "Branding" : "Branding"}
                </DropdownMenuItem>
                {project.is_owner !== false && (
                  <DropdownMenuItem
                    onSelect={duplicateProject}
                    data-testid="duplicate-project-btn"
                    disabled={duplicating}
                    className="cursor-pointer focus:bg-[#FBFAF7]"
                  >
                    {duplicating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin text-[#5C5B5A]" strokeWidth={1.5} />
                    ) : (
                      <Copy className="h-4 w-4 mr-2 text-[#5C5B5A]" strokeWidth={1.5} />
                    )}
                    {lang === "es" ? "Duplicar como nueva versión" : "Duplicate as new version"}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="mx-1 h-6 w-px bg-[#E8E6E1]" aria-hidden="true" />
            <LanguageSwitcher />
            <button
              onClick={logout}
              className="text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors p-1"
              data-testid="logout-btn"
              aria-label="logout"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 self-start">
          {/* Lean/Full toggle */}
          <div className="mb-5 flex items-center gap-2" data-testid="plan-mode-toggle">
            <Overline>{t("lean.label")}</Overline>
            <div className="inline-flex border border-[#E8E6E1] rounded-sm overflow-hidden text-xs">
              <button
                onClick={() => setPlanMode("lean")}
                className={`px-2.5 py-1 tracking-[0.2em] uppercase transition-all ${
                  planMode === "lean" ? "bg-[#2563EB] text-[#F9F8F6]" : "bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
                }`}
                data-testid="plan-mode-lean-btn"
              >
                {t("lean.lean")}
              </button>
              <button
                onClick={() => setPlanMode("full")}
                className={`px-2.5 py-1 tracking-[0.2em] uppercase transition-all ${
                  planMode === "full" ? "bg-[#2563EB] text-[#F9F8F6]" : "bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
                }`}
                data-testid="plan-mode-full-btn"
              >
                {t("lean.full")}
              </button>
            </div>
          </div>

          <Overline>{lang === "es" ? "Módulos" : "Modules"}</Overline>
          <nav className="mt-4 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {visibleModules.map((m, idx) => {
              const Icon = m.icon;
              const isActive = m.key === active;
              return (
                <button
                  key={m.key}
                  onClick={() => setActive(m.key)}
                  className={`flex items-center gap-3 px-4 py-3 text-sm border-l-2 transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-white border-[#2563EB] text-[#2563EB] font-medium"
                      : "border-transparent text-[#5C5B5A] hover:text-[#1A1A1A] hover:bg-white/60"
                  }`}
                  data-testid={`module-tab-${m.key}`}
                >
                  <span className="font-mono text-xs text-[#8A8886]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  <span>{t(`module.${m.key}`)}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Module content */}
        <section ref={moduleRef} className="space-y-8 min-w-0">
          {ActiveComp && (
            <ModuleContainer
              key={active}
              moduleKey={active}
              moduleData={moduleData}
              project={project}
              Component={ActiveComp}
              saveModule={saveModule}
              lang={lang}
            />
          )}
        </section>
      </div>

      {/* Hidden full-plan document for PDF export */}
      <div
        ref={fullPlanRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        <FullPlanDocument project={project} />
      </div>

      {showOneSheet && <OneSheet project={project} onClose={() => setShowOneSheet(false)} />}
      {showShare && <ShareModal projectId={id} onClose={() => setShowShare(false)} lang={lang} />}
      {showCollabs && (
        <CollaboratorsModal
          projectId={id}
          projectName={project.name}
          onClose={() => {
            setShowCollabs(false);
            load(); // refresh project to update collaborator count badge
          }}
          lang={lang}
        />
      )}
    </div>
  );
}

// ===========================================================================
// ModuleContainer — owns the live (in-flight) value for the active module
// and wires it to the auto-save hook. Renders a SaveIndicator chip above
// the module body so users always see persistence state. Modules emit
// changes via `onChange` (preferred); the legacy `onSave` callback is kept
// as a no-op for backwards compatibility with sub-components still using it.
// ===========================================================================
function ModuleContainer({ moduleKey, moduleData, project, Component, saveModule, lang }) {
  const [value, setValue] = useState(moduleData);

  // Re-hydrate when the user switches modules. We also re-hydrate when the
  // server-side moduleData differs from our local in-flight value — that
  // covers AI regenerations and external collaborator edits without
  // clobbering the user's pending keystrokes (auto-save dedups via
  // JSON.stringify).
  useEffect(() => {
    setValue(moduleData);
  }, [moduleKey]);

  useEffect(() => {
    try {
      if (JSON.stringify(moduleData) !== JSON.stringify(value)) {
        setValue(moduleData);
      }
    } catch {
      setValue(moduleData);
    }
  }, [moduleData]);

  const { status, lastSavedAt, flush } = useAutoSave(
    value,
    (v) => saveModule(moduleKey, v),
    { delay: 1500 }
  );

  return (
    <>
      <div
        className="flex items-center justify-end h-5 -mt-2 mb-1 pr-1"
        data-testid="save-indicator-bar"
      >
        <SaveIndicator status={status} lastSavedAt={lastSavedAt} onRetry={flush} lang={lang} />
      </div>
      <Component
        data={value}
        project={project}
        onChange={setValue}
        onSave={setValue}
      />
    </>
  );
}

// ===========================================================================
// ShareModal — generates a public link, shows it with copy-to-clipboard
// ===========================================================================

function ShareModal({ projectId, onClose, lang }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/projects/${projectId}/share`);
        setInfo(res.data);
      } catch {
        setInfo({ token: null });
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  async function createLink() {
    setCreating(true);
    try {
      const res = await api.post(`/projects/${projectId}/share`, { expires_days: 30 });
      setInfo(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  }

  async function revokeLink() {
    if (!window.confirm(lang === "es" ? "¿Revocar el enlace público?" : "Revoke public link?")) return;
    setRevoking(true);
    try {
      await api.delete(`/projects/${projectId}/share`);
      setInfo({ token: null });
      toast.success(lang === "es" ? "Enlace revocado" : "Link revoked");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRevoking(false);
    }
  }

  function copyUrl() {
    const url = `${window.location.origin}/share/plans/${info.token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    toast.success(lang === "es" ? "URL copiada" : "URL copied");
  }

  const shareUrl = info?.token ? `${window.location.origin}/share/plans/${info.token}` : "";

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6" onClick={onClose} data-testid="share-modal">
      <div className="bg-white border border-[#E8E6E1] rounded-sm max-w-xl w-full p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <Overline>{lang === "es" ? "Compartir plan" : "Share plan"}</Overline>
            <h3
              className="mt-2 text-2xl text-[#1A1A1A]"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
            >
              {lang === "es" ? "Enlace público de solo lectura" : "Public read-only link"}
            </h3>
            <p className="text-sm text-[#5C5B5A] mt-2">
              {lang === "es"
                ? "Cualquiera con el enlace verá el plan sin necesidad de cuenta. Caduca en 30 días por defecto."
                : "Anyone with the link sees the plan, no account needed. Defaults to 30-day expiry."}
            </p>
          </div>
          <button onClick={onClose} className="text-[#8A8886] hover:text-[#1A1A1A]" aria-label="close">✕</button>
        </div>

        {loading ? (
          <div className="text-sm text-[#5C5B5A]">{lang === "es" ? "Cargando..." : "Loading..."}</div>
        ) : info?.token ? (
          <div className="space-y-4">
            <div className="bg-[#F5F4F0] border border-[#E8E6E1] rounded-sm p-3 flex items-center gap-2">
              <input
                value={shareUrl}
                readOnly
                onClick={(e) => e.target.select()}
                className="flex-1 bg-transparent text-sm font-mono text-[#1A1A1A] outline-none"
                style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                data-testid="share-url-input"
              />
              <SecondaryButton onClick={copyUrl} testId="share-copy-btn">
                {copied ? <Check className="h-4 w-4" strokeWidth={2} /> : <Copy className="h-4 w-4" strokeWidth={1.5} />}
                {copied ? (lang === "es" ? "Copiada" : "Copied") : (lang === "es" ? "Copiar" : "Copy")}
              </SecondaryButton>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-[#5C5B5A] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              <div>
                <Overline>{lang === "es" ? "Visitas" : "Views"}</Overline>
                <div className="text-lg text-[#1A1A1A] mt-1">{info.views ?? 0}</div>
              </div>
              <div>
                <Overline>{lang === "es" ? "Caduca" : "Expires"}</Overline>
                <div className="text-lg text-[#1A1A1A] mt-1">
                  {info.expires_at
                    ? new Date(info.expires_at).toLocaleDateString(lang === "es" ? "es-ES" : "en-US")
                    : (lang === "es" ? "Nunca" : "Never")}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E8E6E1]">
              <a href={shareUrl} target="_blank" rel="noreferrer noopener">
                <SecondaryButton testId="share-open-btn">
                  {lang === "es" ? "Abrir vista pública" : "Open public view"}
                </SecondaryButton>
              </a>
              <SecondaryButton onClick={revokeLink} disabled={revoking} testId="share-revoke-btn">
                {revoking ? (lang === "es" ? "Revocando…" : "Revoking…") : (lang === "es" ? "Revocar enlace" : "Revoke link")}
              </SecondaryButton>
            </div>
          </div>
        ) : (
          <div>
            <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm p-5 text-sm text-[#5C5B5A]">
              {lang === "es"
                ? "Aún no has compartido este plan. Genera un enlace público para enviarlo a inversores, socios o asesores."
                : "You haven't shared this plan yet. Generate a public link to send to investors, partners or advisors."}
            </div>
            <div className="mt-5 flex justify-end">
              <PrimaryButton onClick={createLink} disabled={creating} testId="share-create-btn">
                <Share2 className="h-4 w-4" strokeWidth={1.5} />
                {creating ? (lang === "es" ? "Generando…" : "Generating…") : (lang === "es" ? "Generar enlace público" : "Generate public link")}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
